import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchOnboardingById } from "services/onboarding.service";
import { Onboarding } from "types/Onboarding";
import { AppDispatch } from "configureStore";

interface OnboardingContainerState {
  isLoading: boolean;
  isError: boolean;
  errMsg: string;
  onboardingEntity: Onboarding | null;
  // The onboarding id of the latest in-flight request. Responses (success or
  // error) only apply if they still match it, so a stale response for an
  // onboarding we've navigated away from can't overwrite the current one or
  // surface a wrong error.
  requestedOnboardingId: string | null;
}

const initialState: OnboardingContainerState = {
  isLoading: true,
  isError: false,
  errMsg: "",
  onboardingEntity: null,
  requestedOnboardingId: null,
};

const OnboardingContainerSlice = createSlice({
  name: "onboardingContainer",
  initialState,
  reducers: {
    setDestroy() {
      return initialState;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    // Marks the start of a fetch: records which onboarding is requested, shows
    // loading, and clears any prior error so a stale error can't block the new
    // onboarding.
    setRequest(state, action: PayloadAction<string>) {
      state.requestedOnboardingId = action.payload;
      state.isLoading = true;
      state.isError = false;
      state.errMsg = "";
    },
    setError(state, action: PayloadAction<{ id: string; error: string }>) {
      if (state.requestedOnboardingId !== action.payload.id) {
        return;
      }
      state.isLoading = false;
      state.isError = true;
      state.errMsg = action.payload.error;
    },
    setData(state, action: PayloadAction<{ id: string; data: Onboarding }>) {
      if (state.requestedOnboardingId !== action.payload.id) {
        return;
      }
      state.onboardingEntity = action.payload.data;
      state.isLoading = false;
      state.isError = false;
      state.errMsg = "";
    },
  },
});

const { actions, reducer } = OnboardingContainerSlice;

export const { setDestroy, setLoading, setRequest, setError, setData } =
  actions;
export default reducer;

export const fetchOnboardingDetails =
  ({ companyId, onboardingId }: { companyId: string; onboardingId: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(setRequest(onboardingId));
    try {
      const data = await fetchOnboardingById(companyId, onboardingId);
      dispatch(setData({ id: onboardingId, data }));
    } catch (e) {
      dispatch(setError({ id: onboardingId, error: "not_fetch" }));
    }
  };
